import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import Steps from "../../../components/Sales/CustomOrder/Steps/Steps";
import { createCustomOrder } from "../../../shared/repos/graphql/customOrder";
import { orderTypeIds } from "../../../shared/constants/order";
import ReCAPTCHAForm from "../../../components/ReCaptcha/ReCaptcha";
import httpStatusConstant from "../../../shared/constants/httpStatus";
import elementConstants from "../../../shared/constants/element";
import * as elementsActions from "../../../redux/actions/elements";
import * as siteSettingsActions from "../../../redux/actions/siteSettings";
import * as siteSettingRepo from "../../../shared/repos/graphql/siteSetting";
import siteSetting from "../../../shared/constants/siteSetting";
import { setSessionToken } from "../../../shared/utilities/config";
import { setCaptchaToken } from "../../../shared/utilities/captcha";
import captchaAction from "../../../shared/constants/captchaAction";

const step1Image = require("./imgs/step1-cookies.png");

const CustomOrderPage = ({
  onOrderSubmit,
  initialFormData,
  useRecaptcha,
  setVerificationToken,
  setVerificationProvider,
  setVerificationAction,
  setUseRecaptcha,
  setDataCollectionDelay
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [errorMessages, setErrorMessages] = useState([]);
  const [errorTitle, setErrorTitle] = useState("");
  const [showCaptchaChallenge, setShowCaptchaChallenge] = useState(false);
  const [hasCaptchaError, setHasCaptchaError] = useState(false);
  const recaptchaComponentRef = useRef(null);
  const [skipGenerateNewToken, setSkipGenerateNewToken] = useState(false);
  const [siteSettingsLoaded, setSiteSettingsLoaded] = useState(false);

  const [formData, setFormData] = useState(
    initialFormData || {
      orderType: "delivery",
      address: null,
      date: new Date(),
      time: null,
      storeData: null,
      addressCoordinates: null,
      products: {},
      additionalProductRequests: null,
      waxBags: false,
      cellophaneBags: false,
      assortment: false,
      iceCream: false,
      iceCreamDescription: null,
      contactInfo: {
        name: null,
        phone: null,
        email: null,
        preferredContactMethod: "phone"
      },
      paymentMethod: null,
      reasonForShopping: null,
      gratuity: {
        type: "percentage",
        amount: "25%"
      }
    }
  );

  // --- 1. Fetch site settings (USE_RECAPTCHA, DATA_COLLECTION_DELAY) like PageContainer ---
  useEffect(() => {
    const loadSiteSettings = async () => {
      try {
        const resp = await siteSettingRepo.getSiteSettings(
          [
            siteSetting.USE_RECAPTCHA,
            siteSetting.DATA_COLLECTION_DELAY_MILLISECONDS
          ],
          true
        );

        if (!resp || !resp.data || !resp.data.siteSettings) {
          setSiteSettingsLoaded(true);
          return;
        }

        const allSettings = resp.data.siteSettings;

        // USE_RECAPTCHA
        const useRecaptchaSetting = allSettings.find(
          s => s.name === siteSetting.USE_RECAPTCHA && s.country_id === null
        );
        if (useRecaptchaSetting?.value) {
          try {
            const json = JSON.parse(useRecaptchaSetting.value);
            // In PageContainer they use elementConstant.consumerPlatform
            const platformKey = elementConstants.consumerPlatform;
            const value = parseInt(json[platformKey], 10) || 0;
            setUseRecaptcha(value);
          } catch (e) {
            setUseRecaptcha(0);
          }
        } else {
          setUseRecaptcha(0);
        }

        // DATA_COLLECTION_DELAY_MILLISECONDS
        const dataCollectionDelaySetting = allSettings.find(
          s =>
            s.name === siteSetting.DATA_COLLECTION_DELAY_MILLISECONDS &&
            s.country_id === null
        );
        if (dataCollectionDelaySetting?.value) {
          const delay = parseInt(dataCollectionDelaySetting.value, 10) || 0;
          setDataCollectionDelay(delay);
        }

        setSiteSettingsLoaded(true);
      } catch (e) {
        setUseRecaptcha(0);
        setSiteSettingsLoaded(true);
      }
    };

    setSessionToken(uuidv4());
    loadSiteSettings();
  }, [setUseRecaptcha, setDataCollectionDelay]);

  // --- 2. Initialize Enterprise reCAPTCHA once siteSettingsLoaded + useRecaptcha === 1 ---
  useEffect(() => {
    let timer;

    if (siteSettingsLoaded && useRecaptcha === 1) {
      const initCaptcha = () => {
        setCaptchaToken(
          setVerificationToken,
          setVerificationAction,
          captchaAction.addressSearching,
          skipGenerateNewToken
        );
      };

      timer = setTimeout(initCaptcha, 500);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [
    siteSettingsLoaded,
    useRecaptcha,
    setVerificationToken,
    setVerificationAction,
    skipGenerateNewToken
  ]);

  const handleFormDataChange = newData => {
    setFormData({ ...formData, ...newData });
  };

  const handleNext = () => {
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (submitData = null) => {
    setErrorMessages([]);
    setErrorTitle("");
    setShowCaptchaChallenge(false);
    setHasCaptchaError(false);

    try {
      const dataToUse = submitData ? { ...formData, ...submitData } : formData;

      const customOrderData = {
        orderType:
          dataToUse.orderType === "delivery"
            ? orderTypeIds.delivery
            : orderTypeIds.pickup,
        deliveryAddress:
          dataToUse.orderType === "delivery" ? dataToUse.address : undefined,
        storeId: dataToUse.storeData?.id || undefined,
        eventDate: new Date(dataToUse.date).toISOString().split("T")[0],
        eventTime: dataToUse.time === "asap" ? null : dataToUse.time,
        assortment: dataToUse.assortment || false,
        products: dataToUse.products,
        additionalProductRequests:
          dataToUse.additionalProductRequests?.trim() || "",
        iceCream: dataToUse.iceCream || false,
        iceCreamDescription: dataToUse.iceCreamDescription?.trim() || "",
        individualWaxWrapped: dataToUse.waxBags || false,
        individualCellophaneWrapped: dataToUse.cellophaneBags || false,
        name: dataToUse.contactInfo.name?.trim() || "",
        phoneNumber: dataToUse.contactInfo.phone?.trim() || "",
        email: dataToUse.contactInfo.email?.trim() || "",
        preferredContactMethod: dataToUse.contactInfo.preferredContactMethod,
        paymentMethod: dataToUse.paymentMethod,
        gratuityPercentage:
          dataToUse.gratuity?.type === "percentage"
            ? parseFloat(dataToUse.gratuity?.amount?.replace("%", ""))
            : undefined,
        gratuityAmount:
          dataToUse.gratuity?.type === "custom"
            ? parseFloat(dataToUse.gratuity?.amount)
            : undefined,
        reasonForShopping: dataToUse.reasonForShopping?.trim() || ""
      };

      const response = await createCustomOrder(customOrderData);

      if (response.data?.createCustomOrder?.success) {
        if (onOrderSubmit) onOrderSubmit(customOrderData);
        setCurrentStep(4);
        setVerificationProvider("");
      } else {
        const messages = response.data?.createCustomOrder?.errors;
        if (Array.isArray(messages) && messages.length > 0) {
          setErrorMessages(messages);
        }
        setErrorTitle(response.data?.createCustomOrder?.message);
      }
    } catch (er) {
      const errorMessage = er?.message || er?.toString() || "";
      const networkError = er?.networkError;
      const statusCode = networkError?.statusCode;
      const hasUnauthorized =
        statusCode === 401 ||
        errorMessage.indexOf(httpStatusConstant.unauthorised) !== -1 ||
        (networkError?.result &&
          JSON.stringify(networkError.result).indexOf(
            httpStatusConstant.unauthorised
          ) !== -1);

      if (hasUnauthorized && useRecaptcha === 1) {
        setHasCaptchaError(true);
        setShowCaptchaChallenge(true);
        setErrorTitle("Please complete the captcha verification to continue.");
      } else {
        setErrorTitle("Error submitting order, Please try after some time.");
      }
    }
  };

  const onVerifyCaptcha = token => {
    if (token) {
      setShowCaptchaChallenge(false);
      setSkipGenerateNewToken(true);
      setVerificationToken(token);
      setVerificationProvider(elementConstants.challengeRecaptcha);
      handleSubmit();
    }
  };

  const renderCaptchaChallenge = () =>
    showCaptchaChallenge && useRecaptcha === 1 ? (
      <div className="flex-1 flex flex-col md:px-10 mt-4">
        <div className="p-4 mt-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400">
          {errorTitle && (
            <div className="mb-4">
              <span className="font-medium">{errorTitle}</span>
            </div>
          )}
          <ReCAPTCHAForm
            ref={recaptchaComponentRef}
            setToken={onVerifyCaptcha}
          />
        </div>
      </div>
    ) : null;

  return (
    <div className="min-h-screen px-6 pt-[40px] pb-[40px] font-congenialBlack bg-gradient-to-b from-[#461D58] to-[#1A0021]">
      <div className="flex max-w-7xl mx-auto">
        {/* Left Section - Image */}
        <div className="hidden ipad:flex relative overflow-hidden items-center justify-center min-h-screen md:w-2/5">
          <div className="relative w-full h-full flex items-start justify-center">
            <img
              src={step1Image}
              alt="Custom Order"
              className="w-full h-auto object-cover object-center transition-transform duration-300 rounded-xl"
              height={622}
              width={500}
            />
          </div>
        </div>

        {/* Right Section - Form */}
        <div className="w-full md:w-3/5 flex flex-col">
          <Steps
            currentStep={currentStep}
            formData={formData}
            onFormDataChange={handleFormDataChange}
            onNext={handleNext}
            onBack={handleBack}
            onSubmit={handleSubmit}
          />

          {errorTitle && !hasCaptchaError && (
            <div className="flex-1 flex flex-col md:px-10 mt-4 space-y-2">
              <div
                className="flex p-4 mt-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400"
                role="alert"
              >
                <div>
                  {errorTitle && (
                    <span className="font-medium">{errorTitle}</span>
                  )}
                  {Array.isArray(errorMessages) && errorMessages.length > 0 && (
                    <ul className="list-disc list-inside">
                      {errorMessages.map(msg => (
                        <li key={msg}>{msg}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Captcha Challenge */}
          {renderCaptchaChallenge()}
        </div>
      </div>
    </div>
  );
};

CustomOrderPage.propTypes = {
  onOrderSubmit: PropTypes.func,
  initialFormData: PropTypes.shape({}),
  useRecaptcha: PropTypes.number,
  setVerificationToken: PropTypes.func,
  setVerificationProvider: PropTypes.func,
  setVerificationAction: PropTypes.func,
  setUseRecaptcha: PropTypes.func.isRequired,
  setDataCollectionDelay: PropTypes.func.isRequired
};

CustomOrderPage.defaultProps = {
  onOrderSubmit: null,
  initialFormData: {},
  useRecaptcha: 0,
  setVerificationToken: () => {},
  setVerificationProvider: () => {},
  setVerificationAction: () => {}
};

export const mapStateToProps = state => {
  const { useRecaptcha } = state.elements;
  return {
    useRecaptcha: useRecaptcha ?? 0
  };
};

export const mapDispatchToProps = dispatch => ({
  setVerificationToken: value =>
    dispatch(elementsActions.setVerificationToken(value)),
  setVerificationProvider: value =>
    dispatch(elementsActions.setVerificationProvider(value)),
  setVerificationAction: value =>
    dispatch(elementsActions.setVerificationAction(value)),
  setUseRecaptcha: value => dispatch(elementsActions.setUseRecaptcha(value)),
  setDataCollectionDelay: value =>
    dispatch(siteSettingsActions.setDataCollectionDelay(value))
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomOrderPage);
